/**
 * Troubleshooting "Common actions" tab: store/action pickers, inputs, and JSON or CSV-upload results.
 */
import { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { SingleValue } from 'react-select';

import {
    DIRECT_RMA_STATUS_VALUES,
    DirectRmaStatus,
    IStore,
    STALE_ENTITY_MODE_VALUES,
    StaleEntityMode,
} from '../../../../services/api/troubleshooting-service';
import { validateEmail } from '../../../../services/helpers';
import CurrentPortalStore from '../../../../stores/current-portal-store';
import currentAccountStore from '../../../../pages/accounts/stores/current-account-store';
import { IOption, SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import { Input } from '../../../../components/inputs/input/input';
import { Button } from '../../../../components/inputs/button/button';
import { Toggle } from '../../../../components/inputs/toggle/toggle';
import JsonFormatter from '../../../../components/json-formatter/json-formatter';
import { ConfirmModal } from '../../../../components/modal/components/confirm-modal/confirm-modal';

import { RMA_STATUS } from '../../../manage-returns/rma-page/rma-types';

import TroubleshootingStore from '../../troubleshooting-page-store';
import { CommonAction } from '../../types/action-types';
import { ActionFactory } from '../../action-registry/action-factory';

import './common-actions-page.scss';

const DIRECT_RMA_STATUS_LABELS: Record<DirectRmaStatus, string> = {
    pending_approval: 'Pending approval',
    approved: 'Approved',
    shipment_received: 'Shipment received',
    done: 'Done',
};

const DIRECT_RMA_STATUS_SELECT_OPTIONS: IOption[] = DIRECT_RMA_STATUS_VALUES.map((status) => ({
    value: status,
    label: DIRECT_RMA_STATUS_LABELS[status],
}));

const STALE_ENTITY_MODE_LABELS: Record<StaleEntityMode, string> = {
    soft_delete: 'Soft delete',
    disable: 'Disable',
    leave: 'Leave',
};

const STALE_ENTITY_MODE_SELECT_OPTIONS: IOption[] = STALE_ENTITY_MODE_VALUES.map((mode) => ({
    value: mode,
    label: STALE_ENTITY_MODE_LABELS[mode],
}));

const CommonActionsPage: React.FC = observer(() => {
    const [chosenStore, setChosenStore] = useState<IOption | null>(null);
    const [chosenAction, setChosenAction] = useState<IOption | undefined>(undefined);
    const [shopifyResponse, setShopifyResponse] = useState<any>(null);
    const [actionInput, setActionInput] = useState({ render: true, value: '', additionalValue: '' });
    const [directStatusOption, setDirectStatusOption] = useState<IOption>(DIRECT_RMA_STATUS_SELECT_OPTIONS[0]);
    const [toggleValue, setToggleValue] = useState(false);
    const [fromStore, setFromStore] = useState<IOption | null>(null);
    const [staleModeOption, setStaleModeOption] = useState<IOption>(STALE_ENTITY_MODE_SELECT_OPTIONS[0]);
    const [json, setJson] = useState('');
    const [jsonExpansionLevel, setJsonExpansionLevel] = useState(1);
    const [confirmationType, setConfirmationType] = useState<string | null>(null);
    const [csvFile, setCsvFile] = useState<File | null>(null);

    useEffect(() => {
        TroubleshootingStore.fetchStores();
        CurrentPortalStore.load();
    }, []);

    const portalId = CurrentPortalStore.currentPortal.id;
    const portalName = CurrentPortalStore.currentPortal.name;
    const portalDisplayName = CurrentPortalStore.currentPortal.displayName;
    const isSysAdmin = currentAccountStore.isSysAdmin;

    const commonActions = useMemo(() => {
        return ActionFactory.getAllCommonActions(isSysAdmin);
    }, [isSysAdmin]);

    const actionOptions: IOption[] = commonActions.map(action => ({
        value: action.id,
        label: action.label,
    }));

    useEffect(() => {
        if (chosenStore?.value !== portalId && portalId && portalName) {
            setChosenStore({ 
                value: portalId, 
                label: portalName + (portalDisplayName ? ' (' + portalDisplayName + ')' : '') 
            });
        }
    }, [portalId, portalName, portalDisplayName, chosenStore?.value]);

    const __shopifyResponse = TroubleshootingStore.shopifyResponse;

    useEffect(() => {
        setJson('');
        setShopifyResponse(toJS(TroubleshootingStore.shopifyResponse));
    }, [__shopifyResponse]);

    const generateSelectValues = (): IOption[] | [] => {
        return TroubleshootingStore.stores?.map((el: IStore): IOption => {
            return {
                value: el.id,
                label: el.name + (el.display_name ? ' (' + el.display_name + ')' : '')
            }
        }) || [];
    }

    const onStoreChangeHandler = (val: SingleValue<IOption>) => {
        if (val) setChosenStore(val);
    }

    const onFromStoreChangeHandler = (val: SingleValue<IOption>) => {
        if (val) setFromStore(val);
    }

    const actionChangeHandler = (val: SingleValue<IOption>) => {
        if (val) {
            setChosenAction(val);
            const actionMetadata = ActionFactory.getCommonAction(val.value as CommonAction);
            setCsvFile(null);
            TroubleshootingStore.resetShopifyResponse();
            setActionInput(() => ({
                value: '',
                additionalValue: '',
                render: Boolean(
                    actionMetadata?.requiresInput
                    || actionMetadata?.requiresStoresSelect
                    || actionMetadata?.requiresFileUpload
                    || actionMetadata?.requiresDirectRmaStatusTarget
                ),
            }));
            setDirectStatusOption(DIRECT_RMA_STATUS_SELECT_OPTIONS[0]);
            setToggleValue(false);
            setFromStore(null);
            setStaleModeOption(STALE_ENTITY_MODE_SELECT_OPTIONS[0]);
        }
    }

    const actionInputHandler = (val: string) => {
        setActionInput((prev) => ({ ...prev, value: val }));
    }

    const additionalActionInputHandler = (val: string) => {
        setActionInput((prev) => ({ ...prev, additionalValue: val }));
    }

    const onEnterKeyPressSubmit = () => {
        performRequest();
    }

    const getActionMetadata = (): ReturnType<typeof ActionFactory.getCommonAction> => {
        if (!chosenAction?.value) return undefined;
        return ActionFactory.getCommonAction(chosenAction.value as CommonAction);
    }

    const renderActionInput = () => {
        const actionMetadata = getActionMetadata();
        if (!actionMetadata) return null;

        if (actionMetadata.requiresFileUpload) {
            return (
                <div className="controls">
                    <p className="head5">CSV file</p>
                    <input
                        type="file"
                        accept=".csv,text/csv,application/csv"
                        disabled={TroubleshootingStore.csvUploadInProgress}
                        onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
                    />
                </div>
            );
        }

        if (actionMetadata?.requiresStoresSelect) {
            return (
                <>
                    <div className="controls">
                        <p className="head5">FROM Store</p>
                        <SelectDropdown
                            required
                            value={fromStore || undefined}
                            placeholder="Select store to copy settings from"
                            options={generateSelectValues()}
                            onChange={onFromStoreChangeHandler}
                        />
                    </div>
                    {actionMetadata.id === CommonAction.DUPLICATE_STORE ? (
                        <div className="controls">
                            <p className="head5">Stale entity mode</p>
                            <SelectDropdown
                                required
                                value={staleModeOption}
                                placeholder="Select stale entity mode"
                                options={STALE_ENTITY_MODE_SELECT_OPTIONS}
                                onChange={(val: SingleValue<IOption>) => {
                                    if (val) {
                                        setStaleModeOption(val);
                                    }
                                }}
                            />
                        </div>
                    ) : null}
                </>
            )
        }

        if (actionMetadata?.inputLabel) {
            return (
                <>
                    <div className="controls">
                        <p className="head5">{actionMetadata.inputLabel}</p>
                        <Input
                            required
                            value={actionInput?.value}
                            onChange={actionInputHandler}
                            onSubmit={onEnterKeyPressSubmit}
                        />
                    </div>
                    {actionMetadata.requiresDirectRmaStatusTarget ? (
                        <div className="controls">
                            <p className="head5">Target status</p>
                            <SelectDropdown
                                required
                                value={directStatusOption}
                                placeholder="Select target status"
                                options={DIRECT_RMA_STATUS_SELECT_OPTIONS}
                                onChange={(val: SingleValue<IOption>) => {
                                    if (val) {
                                        setDirectStatusOption(val);
                                    }
                                }}
                            />
                        </div>
                    ) : null}
                </>
            );
        }

        return null;
    }

    const renderAdditionalActionInput = () => {
        const actionMetadata = getActionMetadata();
        if (!actionMetadata?.requiresAdditionalInput) {
            return null;
        }

        return (
            <div className="controls">
                <p className="head5 head5_dense">{actionMetadata.additionalInputLabel}</p>
                <Input
                    value={actionInput.additionalValue}
                    onChange={additionalActionInputHandler}
                />
            </div>
        );
    }

    const renderToggle = () => {
        const actionMetadata = getActionMetadata();
        if (!actionMetadata?.requiresToggle) {
            return null;
        }

        return (
            <div className="controls">
                <Toggle
                    label={actionMetadata.toggleLabel || ''}
                    checked={toggleValue}
                    onChange={setToggleValue}
                />
            </div>
        );
    }
    
    const canPerformAction = useMemo(() => {
        const metadata = getActionMetadata();
        if (!chosenStore?.value || !chosenAction?.value || !metadata) {
            return false;
        }
        if (metadata?.requiresStoresSelect && !fromStore?.value) {
            return false;
        }
        if (metadata?.requiresInput && !actionInput?.value) {
            return false;
        }
        if (metadata?.requiresFileUpload && !csvFile) {
            return false;
        }
        return true;
    }, [chosenStore, chosenAction, fromStore, actionInput.value, csvFile, getActionMetadata]);

    const isSysAdminAction = getActionMetadata()?.isSysAdminOnly ?? false;
    const isUploadCsvAction = chosenAction?.value === CommonAction.TROUBLESHOOTING_UPLOAD_CSV;
    const csvResult = isUploadCsvAction && shopifyResponse?.data?.url
        ? shopifyResponse.data as { url: string; expiresAt: string }
        : null;
    const csvError = isUploadCsvAction && shopifyResponse?.error
        ? String(shopifyResponse.error)
        : null;

    const performRequest = () => {
        if (!chosenStore?.value || !chosenAction?.value) return;

        const actionId = chosenAction.value as CommonAction;
        setJsonExpansionLevel(2);

        if (actionInput?.render) {
            setJson('Loading...');
            switch (actionId) {
                case CommonAction.FETCH_CUSTOMER:
                    TroubleshootingStore.fetchCustomer(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_DISCOUNT_CODE:
                    TroubleshootingStore.fetchDiscountCode(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_ORDER:
                    TroubleshootingStore.fetchOrder(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_ORDER_BY_ID:
                    TroubleshootingStore.fetchOrderById(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_ORDER_BY_RMA_ID:
                    TroubleshootingStore.fetchOrderByRMAId(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_DRAFT_ORDER_BY_ID:
                    TroubleshootingStore.fetchDraftOrderById(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_DRAFT_ORDER:
                    TroubleshootingStore.fetchDraftOrder(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_PRODUCT:
                    TroubleshootingStore.fetchProduct(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_VARIANTS:
                    TroubleshootingStore.fetchVariants(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_FULFILLMENT_BY_ID:
                    TroubleshootingStore.fetchFulfillmentById(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_RMA:
                    TroubleshootingStore.fetchRMA(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.FETCH_ORDER_ELIGIBILITY_LEGACY:
                    TroubleshootingStore.fetchOrderEligibilityLegacy(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.TROUBLESHOOTING_UPLOAD_CSV:
                    if (!csvFile) {
                        setJson('Select a CSV file.');
                        return;
                    }
                    void TroubleshootingStore.uploadTroubleshootingCsv(chosenStore.value, csvFile);
                    break;
                case CommonAction.BULK_RMA_DONE:
                    TroubleshootingStore.bulkUpdateRmaStatusToDone(chosenStore.value, actionInput.value, actionInput.additionalValue);
                    break;
                case CommonAction.BULK_RMA_APPROVED:
                    TroubleshootingStore.bulkUpdateRmaStatus(chosenStore.value, actionInput.value, RMA_STATUS.APPROVED);
                    break;
                case CommonAction.BULK_RMA_CANCELLED:
                    TroubleshootingStore.bulkUpdateRmaStatus(chosenStore.value, actionInput.value, RMA_STATUS.CANCELED);
                    break;
                case CommonAction.CHANGE_RMA_STATUS_DIRECTLY: {
                    const targetStatus = directStatusOption.value as DirectRmaStatus;
                    void TroubleshootingStore.bulkUpdateRmaStatusDirectly(chosenStore.value, actionInput.value, targetStatus);
                    break;
                }
                case CommonAction.CREATE_SECONDARY_PORTAL:
                    TroubleshootingStore.createSecondaryPortal(chosenStore.value, actionInput.value);
                    break;
                case CommonAction.TRANSFER_REJECTED_RMA_TO_CANCELED:
                    setConfirmationType(CommonAction.TRANSFER_REJECTED_RMA_TO_CANCELED);
                    break;
                case CommonAction.COPY_STORE_SETTINGS:
                    setConfirmationType(CommonAction.COPY_STORE_SETTINGS);
                    break;
                case CommonAction.DUPLICATE_STORE:
                    setConfirmationType(CommonAction.DUPLICATE_STORE);
                    break;
                case CommonAction.CHANGE_STORE_OWNER: {
                    const email = actionInput.value.trim();
                    if (!validateEmail(email)) {
                        setJson('Invalid email format. Please enter a valid email address.');
                        return;
                    }

                    setConfirmationType(CommonAction.CHANGE_STORE_OWNER);
                    break;
                }
                case CommonAction.REMOVE_USER_FROM_STORE: {
                    const email = actionInput?.value?.trim();
                    if (!validateEmail(email)) {
                        setJson('Invalid email format. Please enter a valid email address.');
                        return;
                    }

                    setConfirmationType(CommonAction.REMOVE_USER_FROM_STORE);
                    break;
                }
                case CommonAction.BULK_RMA_AUTO_REFUND:
                    setConfirmationType(CommonAction.BULK_RMA_AUTO_REFUND);
                    break;
            }
        } else {
            setJson('Loading...');
            switch (actionId) {
                case CommonAction.FETCH_SUPERADMIN_ACTIONS:
                    TroubleshootingStore.fetchSuperAdminActions(chosenStore.value);
                    break;
                case CommonAction.CLEAR_TRANSLATIONS_CACHE:
                    TroubleshootingStore.clearTranslationsCache(chosenStore.value);
                    break;
            }
        }
    }

    const copyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(shopifyResponse) || '')
            .catch(err => {
                console.log('Something went wrong', err);
            });
    }

    const confirmTransferRejectedRmaToCanceled = () => {
        if (chosenStore?.value && actionInput?.value) {
            TroubleshootingStore.transferRejectedRmaToCanceled(chosenStore.value, actionInput.value);
            setConfirmationType(null);
        }
    }

    const confirmCopyStoreSettings = () => {
        if (chosenStore?.value && fromStore?.value) {
            TroubleshootingStore.copyStoreSettings(chosenStore.value, fromStore.value);
            setConfirmationType(null);
        }
    }

    const confirmDuplicateStore = () => {
        if (chosenStore?.value && fromStore?.value) {
            TroubleshootingStore.duplicateStore(chosenStore.value, fromStore.value, staleModeOption.value as StaleEntityMode);
            setConfirmationType(null);
        }
    }

    const confirmRemoveUserFromStore = () => {
        const email = actionInput?.value?.trim();
        if (chosenStore?.value && email) {
            TroubleshootingStore.removeUserFromStore(chosenStore.value, email);
            setConfirmationType(null);
        }
    }

    const confirmChangeStoreOwner = () => {
        const email = actionInput?.value?.trim();
        if (chosenStore?.value && email) {
            TroubleshootingStore.changeStoreOwner(chosenStore.value, email);
            setConfirmationType(null);
        }
    }

    const confirmBulkAutoRefundRmas = () => {
        if (chosenStore?.value && actionInput?.value) {
            TroubleshootingStore.bulkAutoRefundRmas(chosenStore.value, actionInput.value, toggleValue);
            setConfirmationType(null);
        }
    }

    const renderConfirmationModal = () => {
        if (confirmationType === CommonAction.TRANSFER_REJECTED_RMA_TO_CANCELED) {
            return (
                <ConfirmModal
                    title={"Change RMA status to canceled"}
                    text=""
                    implicationText={<>Note, this action applies only to RMA(s) with <strong>Rejected status</strong>.</>}
                    questionText={"Are you sure you want to cancel the selected RMA(s)?"}
                    cancelButton={{
                        title: "Cancel",
                        onClick: () => setConfirmationType(null)
                    }}
                    okButton={{
                        title: "Confirm",
                        onClick: confirmTransferRejectedRmaToCanceled
                    }}
                />
            )
        }

        if (confirmationType === CommonAction.COPY_STORE_SETTINGS) {
            return (
                <ConfirmModal
                    title={"Copy Store Settings"}
                    text=""
                    implicationText={<>Note: this action will copy settings from <strong>"{fromStore?.label ?? fromStore?.value}"</strong> to <strong>"{chosenStore?.label ?? chosenStore?.value}"</strong> and <mark><em>may overwrite the target store settings</em>.</mark></>}
                    questionText={"Are you sure you want to copy the store settings?"}
                    cancelButton={{
                        title: "Cancel",
                        onClick: () => setConfirmationType(null)
                    }}
                    okButton={{
                        title: "Confirm",
                        onClick: confirmCopyStoreSettings
                    }}
                />
            )
        }

        if (confirmationType === CommonAction.DUPLICATE_STORE) {
            return (
                <ConfirmModal
                    title={"Duplicate Store"}
                    text=""
                    implicationText={<>Note: this action will duplicate the full configuration from <strong>"{fromStore?.label ?? fromStore?.value}"</strong> to <strong>"{chosenStore?.label ?? chosenStore?.value}"</strong> and <mark><em>may overwrite the target store settings</em>.</mark></>}
                    questionText={"Are you sure you want to duplicate the store?"}
                    cancelButton={{
                        title: "Cancel",
                        onClick: () => setConfirmationType(null)
                    }}
                    okButton={{
                        title: "Confirm",
                        onClick: confirmDuplicateStore
                    }}
                />
            )
        }

        if (confirmationType === CommonAction.REMOVE_USER_FROM_STORE) {
            const email = actionInput?.value?.trim();
            return (
                <ConfirmModal
                    title={"Remove User From Store"}
                    text=""
                    questionText={
                        <>
                            Are you sure you want to remove the user <strong>{email}</strong> from the store 
                            <strong>"{chosenStore?.label ?? chosenStore?.value}"</strong>?
                        </>
                    }
                    cancelButton={{
                        title: "Cancel",
                        onClick: () => setConfirmationType(null)
                    }}
                    okButton={{
                        title: "Confirm",
                        onClick: confirmRemoveUserFromStore
                    }}
                />
            )
        }

        if (confirmationType === CommonAction.CHANGE_STORE_OWNER) {
            const email = actionInput?.value?.trim();
            return (
                <ConfirmModal
                    title={"Change Store Owner"}
                    text=""
                    questionText={
                        <>
                            Are you sure you want to change the owner of the store <strong>"{chosenStore?.label ?? chosenStore?.value}"</strong> to <strong>{email}</strong>?
                        </>
                    }
                    cancelButton={{
                        title: "Cancel",
                        onClick: () => setConfirmationType(null)
                    }}
                    okButton={{
                        title: "Confirm",
                        onClick: confirmChangeStoreOwner
                    }}
                />
            )
        }

        if (confirmationType === CommonAction.BULK_RMA_AUTO_REFUND) {
            return (
                <ConfirmModal
                    title={"Auto Refund RMAs"}
                    text=""
                    questionText={
                        <>
                            Are you sure you want to auto refund the selected RMA(s)?
                        </>
                    }
                    cancelButton={{
                        title: "Cancel",
                        onClick: () => setConfirmationType(null)
                    }}
                    okButton={{
                        title: "Confirm",
                        onClick: confirmBulkAutoRefundRmas
                    }}
                />
            )
        }

        return null;
    };

    return (
        <>
            <div className="common-actions-page">
                <div className="content">
                    <div className="flex1">
                        <div className="card">
                            <h4>Request Information</h4>
                            <div className="controls">
                                <p className="head5">Store</p>
                                <SelectDropdown
                                    required
                                    value={chosenStore || undefined}
                                    placeholder="Type to find and select store"
                                    options={generateSelectValues()}
                                    onChange={onStoreChangeHandler}
                                />
                            </div>
                            <div className="controls">
                                <p className="head5">Action</p>
                                <SelectDropdown
                                    required
                                    value={chosenAction}
                                    options={actionOptions}
                                    onChange={actionChangeHandler}
                                />
                            </div>
                            {renderActionInput()}
                            {renderAdditionalActionInput()}
                            {renderToggle()}
                            <Button
                                disabled={!canPerformAction || TroubleshootingStore.csvUploadInProgress}
                                text={isSysAdminAction ? 'Apply' : 'Fetch'}
                                onClick={() => performRequest()}
                            />
                        </div>
                    </div>

                    <div className="flex1">
                        <div className="card">
                            <div className="controls">
                                <h4>Results</h4>
                                <div>
                                    <Button onClick={() => setJsonExpansionLevel(Infinity)} text="Expand all" />
                                    <Button onClick={() => copyJson()} text="Copy" />
                                </div>
                            </div>
                            {
                                csvResult ? (
                                    <div className="controls">
                                        <p className="head5">Note: URL will be available for 24 hours.</p>
                                        <p className="head5">expiresAt (UTC): {csvResult.expiresAt}</p>
                                        <p className="head5">URL</p>
                                        <input readOnly className="clipboard-text" value={csvResult.url} />
                                        <Button
                                            text="Copy URL"
                                            onClick={() => { void navigator.clipboard.writeText(csvResult.url); }}
                                        />
                                    </div>
                                ) : csvError ? (
                                    <p className="clipboard-text">{csvError}</p>
                                ) : json
                                    ? <p className="clipboard-text">{json}</p>
                                    : <JsonFormatter expansionLevel={jsonExpansionLevel} json={shopifyResponse} />
                            }
                        </div>
                    </div>
                </div>
            </div>

            {confirmationType ? renderConfirmationModal() : null}
        </>
    );
});

export default CommonActionsPage;

